
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { useNotificationSounds } from '@/hooks/useNotificationSounds';
import { Bell, Volume2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NotificationSoundType } from '@/types/notificationSounds';

export const NotificationSoundSettings = () => {
  const { preferences, updatePreferences } = useNotificationSounds();

  const notificationTypes: { type: NotificationSoundType; label: string }[] = [
    { type: 'success', label: 'Success Notifications' },
    { type: 'error', label: 'Error Notifications' },
    { type: 'warning', label: 'Warning Notifications' },
    { type: 'info', label: 'Info Notifications' },
    { type: 'document', label: 'Document Notifications' },
    { type: 'payment', label: 'Payment Notifications' },
    { type: 'attendance', label: 'Attendance Notifications' }
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5" />
          Notification Sound Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <Label htmlFor="sound-enabled">Enable Notification Sounds</Label>
          <Switch
            id="sound-enabled"
            checked={preferences.enabled}
            onCheckedChange={(checked) => updatePreferences({ enabled: checked })}
          />
        </div>

        {preferences.enabled && (
          <>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Volume2 className="h-4 w-4" />
                <Label>Master Volume</Label>
              </div>
              <Slider
                value={[preferences.volume * 100]}
                onValueChange={([value]) => updatePreferences({ volume: value / 100 })}
                max={100}
                step={1}
              />
            </div>

            <div className="space-y-3">
              <Label>Quiet Hours</Label>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="mute-start">Start Time</Label>
                  <Select
                    value={preferences.muteTimeStart || ""}
                    onValueChange={(value) => updatePreferences({ muteTimeStart: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select start time" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }).map((_, i) => (
                        <SelectItem key={i} value={`${i.toString().padStart(2, '0')}:00`}>
                          {`${i.toString().padStart(2, '0')}:00`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mute-end">End Time</Label>
                  <Select
                    value={preferences.muteTimeEnd || ""}
                    onValueChange={(value) => updatePreferences({ muteTimeEnd: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select end time" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 24 }).map((_, i) => (
                        <SelectItem key={i} value={`${i.toString().padStart(2, '0')}:00`}>
                          {`${i.toString().padStart(2, '0')}:00`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <Label>Notification Types</Label>
              {notificationTypes.map(({ type, label }) => (
                <div key={type} className="flex items-center justify-between">
                  <Label htmlFor={`sound-${type}`}>{label}</Label>
                  <Switch
                    id={`sound-${type}`}
                    checked={Boolean(preferences.sounds?.[type])}
                    onCheckedChange={(checked) => {
                      // Create a copy of the current sounds record
                      const updatedSounds = { ...preferences.sounds } as Record<NotificationSoundType, string>;
                      
                      if (checked) {
                        updatedSounds[type] = `${type}.mp3`;
                      } else {
                        // Handle the case where all notification types need to be present
                        // Instead of deleting, we can set to empty string to disable
                        updatedSounds[type] = '';
                      }
                      
                      updatePreferences({ sounds: updatedSounds });
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between">
              <Label htmlFor="workdays-only">Workdays Only</Label>
              <Switch
                id="workdays-only"
                checked={preferences.workDaysOnly || false}
                onCheckedChange={(checked) => updatePreferences({ workDaysOnly: checked })}
              />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
